import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { RunAnswer, RunDetail, RunStatus, RunSummary, RunsSummary } from '@blesk/contracts';
import { apiGet, type QueryParams } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import { useProjectOptions, timezoneOf } from '../../shared/options';
import { CHANNEL_LABEL, RUN_STATUS_LABEL } from '../../shared/labels';
import { formatInProjectTz, periodKeyLabel } from '../../shared/format';
import { useSession } from '../../shared/session';
import { dayStartIso, useUrlFilter, type SectionSearch } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import {
  Dialog,
  EmptyRow,
  ErrorNote,
  ExportCsvLink,
  Pager,
  PeriodFilter,
  StatToggle,
  orDash,
} from '../shell/ui';
import {
  ChartCard,
  ProjectBars,
  RatingColumns,
  StatusSplit,
  TREND_STEP_HINT,
  TrendLine,
  bucketEndIso,
  bucketLabel,
  percent,
  plural,
  score,
  type ProjectChartMetric,
} from '../shell/charts';

/** §9.3, §13 ТЗ. Раздел «Результаты опросов». */
export function ResultsPage() {
  return (
    <PermissionGate permission="results.read">
      <ResultsInner />
    </PermissionGate>
  );
}

function ResultsInner() {
  const session = useSession();
  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [status, setStatus] = useUrlFilter('status');
  const [periodKey, setPeriodKey] = useUrlFilter('periodKey');
  const [hasIncident, setHasIncident] = useUrlFilter('hasIncident');
  const [from, setFrom] = useUrlFilter('from');
  const [to, setTo] = useUrlFilter('to');
  // Открытая карточка живёт в адресе: ссылка из инцидента ведёт сразу в неё,
  // а адрес с открытой карточкой можно переслать и обновить (§13, §14).
  const [cardId, setCardId] = useUrlFilter('entityId');

  const projects = useProjectOptions();
  // один объект отбора на таблицу и на выгрузку (§14, E9): разойтись им негде
  const filters = { projectId, status, periodKey, hasIncident, from, to };
  const list = usePagedList<RunSummary>('results', '/api/results', filters);
  // Сводка одна на экран: тулбар берёт из неё список периодов, панель ниже —
  // показатели. Ключ запроса общий, поэтому на сервер уходит один вызов.
  const summary = useRunsSummary(filters);
  const periods = usePeriodOptions(summary.data, periodKey);

  const zoneOf = (run: RunSummary) =>
    timezoneOf(projects.data, run.projectId, session.defaultTimezone);

  // Обе границы — одной навигацией: два вызова подряд читали бы один и тот же
  // снимок адреса, и вторая граница затирала бы первую
  const navigate = useNavigate();
  const setRange = (nextFrom: string, nextTo: string) => {
    void navigate({
      to: '.',
      search: (previous: SectionSearch) => ({
        ...previous,
        from: nextFrom || undefined,
        to: nextTo || undefined,
      }),
      replace: true,
    });
  };

  return (
    <>
      <h1>Результаты опросов</h1>
      <p className="muted">Ответы привязаны к версии анкеты, объекту, администратору и периоду.</p>

      <div className="toolbar">
        <label>
          Объект
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(RUN_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Период
          {/* Список, а не ввод: ключ периода (§9.2) наизусть не помнят,
              а свободная строка молча даёт пустую таблицу вместо отбора. */}
          <select value={periodKey} onChange={(event) => setPeriodKey(event.target.value)}>
            <option value="">Все периоды</option>
            {periods.map((key) => (
              <option key={key} value={key}>
                {periodKeyLabel(key)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Негатив
          <select value={hasIncident} onChange={(event) => setHasIncident(event.target.value)}>
            <option value="">Все</option>
            <option value="true">Только с инцидентом</option>
            <option value="false">Без инцидента</option>
          </select>
        </label>
        <PeriodFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <span className="toolbar__spacer" />
        <ExportCsvLink path="/api/results/export" filters={filters} />
      </div>

      <ErrorNote error={list.error} />

      <ResultsSummary
        filters={filters}
        status={status}
        hasIncident={hasIncident}
        from={from}
        to={to}
        onStatus={setStatus}
        onHasIncident={setHasIncident}
        onProject={setProjectId}
        onRange={setRange}
      />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Объект и администратор</th>
              <th>Период</th>
              <th>Канал</th>
              <th>Статус</th>
              {/* Негатив — рядом со статусом, а не в конце строки: ради него
                  в раздел и приходят, а последняя колонка уезжает за прокрутку. */}
              <th>Негатив</th>
              {/* Срок и завершение — одна пара «успел или нет», версия анкеты
                  живёт при периоде, администратор — при объекте: раздельными
                  колонками эта четвёрка стоила 400 px (замер 18.08.2026). */}
              <th>Срок и завершение</th>
              <th>Напоминаний</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={8} loading={list.isPending} />}
            {list.items.map((run) => (
              <tr key={run.id}>
                <td>
                  {run.projectName}
                  <div className="cell-note">{run.personName}</div>
                </td>
                <td>
                  {periodKeyLabel(run.periodKey)}
                  <div className="cell-note">версия {run.version}</div>
                </td>
                <td>{CHANNEL_LABEL[run.channel as keyof typeof CHANNEL_LABEL] ?? run.channel}</td>
                <td>
                  {RUN_STATUS_LABEL[run.status]}
                  {run.lateAnswer && (
                    <span className="badge badge--warn cell-mark__badge">поздний ответ</span>
                  )}
                </td>
                <td>{run.hasIncident ? <span className="badge badge--err">есть</span> : '—'}</td>
                <td>
                  {formatInProjectTz(run.dueAt, zoneOf(run))}
                  <div className="cell-note">
                    завершён: {formatInProjectTz(run.completedAt, zoneOf(run))}
                  </div>
                </td>
                <td>{run.remindersSent}</td>
                <td className="row-actions">
                  <button type="button" onClick={() => setCardId(run.id)}>
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {cardId && <RunCard id={cardId} onClose={() => setCardId('')} />}
    </>
  );
}

/**
 * §13 ТЗ. Периоды, которые реально есть в отборе, — для фильтра «Период».
 *
 * Приходят фасетом сводки: сервер считает их по тому же отбору, но без самого
 * `periodKey`, иначе после выбора список схлопнулся бы до одного пункта и
 * перейти из него было бы некуда. Отдельного запроса раздел не делает —
 * сводка и так одна на экран.
 */
function useRunsSummary(filters: QueryParams) {
  return useQuery({
    queryKey: ['results', 'summary', filters],
    queryFn: () => apiGet<RunsSummary>('/api/results/summary', filters),
    placeholderData: keepPreviousData,
  });
}

function usePeriodOptions(summary: RunsSummary | undefined, periodKey: string): string[] {
  const keys = summary?.periodFacets ?? [];
  // фасет считается по запускам, а выбранный период мог из отбора выпасть;
  // убирать из списка то, что уже выбрано, нельзя — снять отбор станет нечем
  return periodKey && !keys.includes(periodKey) ? [periodKey, ...keys] : keys;
}

/**
 * Плитки по статусам. Набор и подписи те же, что на дашборде (§14): человек
 * приходит из плитки дашборда и обязан увидеть здесь тот же статус под тем же
 * именем, поэтому подпись берётся из общего словаря, а не пишется рядом.
 */
const STATUS_TILES: ReadonlyArray<{
  status: RunStatus;
  key: 'scheduled' | 'sent' | 'completed' | 'expired';
}> = [
  { status: 'SCHEDULED', key: 'scheduled' },
  { status: 'SENT', key: 'sent' },
  { status: 'COMPLETED', key: 'completed' },
  { status: 'EXPIRED', key: 'expired' },
];

/**
 * §13, §14 ТЗ. Сводка и диаграммы над таблицей раздела.
 *
 * Считает сервер по тому же отбору, что и таблицу (`/api/results/summary`):
 * плитка «завершено 12» над списком из восьми строк — это не украшение,
 * а неверная цифра. Фильтры сюда приходят тем же объектом, что уходит
 * в список и в выгрузку.
 *
 * Плитки-переключатели показывают `statusFacets` — тот же отбор без фильтра
 * статуса: внутри отбора «завершён» счётчик просроченных всегда ноль, и
 * переход из него выглядел бы сломанным.
 */
function ResultsSummary({
  filters,
  status,
  hasIncident,
  from,
  to,
  onStatus,
  onHasIncident,
  onProject,
  onRange,
}: {
  filters: QueryParams;
  status: string;
  hasIncident: string;
  from: string;
  to: string;
  onStatus: (status: string) => void;
  onHasIncident: (hasIncident: string) => void;
  onProject: (projectId: string) => void;
  onRange: (from: string, to: string) => void;
}) {
  const [metric, setMetric] = useState<ProjectChartMetric>('average');

  const summary = useRunsSummary(filters);

  const data = summary.data;
  if (summary.error) return <ErrorNote error={summary.error} />;
  if (!data) return <p className="muted">Считаем сводку…</p>;

  const { runs, statusFacets, ratings, incidentFacets, byProject } = data;
  const single = byProject.length === 1 ? byProject[0] : undefined;

  return (
    <section className="section-block summary" aria-label="Сводка по отбору">
      <h2>
        Сводка по отбору{single && ` · ${single.projectName}`}{' '}
        <span className="muted">
          {runs.total === 0
            ? 'запусков нет'
            : plural(runs.total, ['запуск', 'запуска', 'запусков'])}
        </span>
      </h2>

      <div className="stat-grid">
        <StatToggle
          value={statusFacets.total}
          label="Все запуски"
          active={status === ''}
          onClick={() => onStatus('')}
        />
        {STATUS_TILES.map((tile) => (
          <StatToggle
            key={tile.status}
            value={statusFacets[tile.key]}
            label={RUN_STATUS_LABEL[tile.status]}
            active={status === tile.status}
            onClick={() => onStatus(status === tile.status ? '' : tile.status)}
          />
        ))}
        {/* Доля ответивших и средняя оценка остаются числами: фильтра, который
            они бы включали, в разделе нет, а плитка-переключатель без отбора
            обещает переход в никуда. */}
        <div className="stat">
          <div className="stat__value">{percent(runs.responseRate)}</div>
          <div className="stat__label">Доля ответивших</div>
        </div>
        <div className="stat">
          <div className="stat__value">{score(ratings.average)}</div>
          <div className="stat__label">
            Средняя оценка · {plural(ratings.answers, ['ответ', 'ответа', 'ответов'])}
          </div>
        </div>
        {/* Число — по отбору без самого «наличия негатива»: иначе при отборе
            «без инцидента» плитка показывала бы ноль и тут же предлагала
            перейти к инцидентам. */}
        <StatToggle
          value={incidentFacets.total}
          label={`Негативных инцидентов · открытых ${incidentFacets.open}`}
          active={hasIncident === 'true'}
          onClick={() => onHasIncident(hasIncident === 'true' ? '' : 'true')}
        />
      </div>

      {runs.total === 0 ? (
        <p className="muted">За выбранный отбор данных нет — диаграммы строить не по чему.</p>
      ) : (
        <>
          <StatusSplit counts={runs} />

          <div className="chart-grid">
            <ChartCard
              title="Распределение оценок"
              hint={
                ratings.answers > 0
                  ? plural(ratings.answers, ['ответ', 'ответа', 'ответов'])
                  : 'оценок нет'
              }
            >
              <RatingColumns distribution={ratings.distribution} answers={ratings.answers} />
            </ChartCard>

            <ChartCard title="Динамика средней оценки" hint={TREND_STEP_HINT[ratings.trendStep]}>
              <TrendLine points={ratings.trend} step={ratings.trendStep} />
              {/* Точка SVG отбором быть не может, поэтому корзины идут рядом
                  отдельным рядом. На дашборде это ссылки в раздел, здесь список
                  уже на экране — переключаются границы «С…По» таблицы, чтобы
                  переход «из показателя в строки» работал одинаково в обе стороны. */}
              {ratings.trend.length > 0 && (
                <p className="chart__periods">
                  {ratings.trend.map((point) => {
                    const bucketFrom = dayStartIso(point.bucket);
                    const bucketTo = bucketEndIso(point.bucket, ratings.trendStep);
                    const active = from === bucketFrom && to === bucketTo;
                    return (
                      <button
                        key={point.bucket}
                        type="button"
                        className="link-button"
                        aria-pressed={active}
                        onClick={() =>
                          active ? onRange('', '') : onRange(bucketFrom, bucketTo)
                        }
                      >
                        {bucketLabel(point.bucket, ratings.trendStep)}
                      </button>
                    );
                  })}
                </p>
              )}
            </ChartCard>
          </div>

          {byProject.length > 1 && (
            <ChartCard
              title="Объекты"
              hint={
                <label>
                  Сначала
                  <select
                    value={metric}
                    onChange={(event) => setMetric(event.target.value as ProjectChartMetric)}
                  >
                    <option value="average">с худшей оценкой</option>
                    <option value="responseRate">с низкой долей ответа</option>
                  </select>
                </label>
              }
            >
              <ProjectBars rows={byProject} metric={metric} onPick={onProject} />
            </ChartCard>
          )}
        </>
      )}
    </section>
  );
}

function RunCard({ id, onClose }: { id: string; onClose: () => void }) {
  const run = useQuery({
    queryKey: ['results', 'detail', id],
    queryFn: () => apiGet<RunDetail>(`/api/results/${id}`),
  });

  const data = run.data;

  return (
    <Dialog
      title={data ? `Опрос · ${data.projectName} · ${periodKeyLabel(data.periodKey)}` : 'Опрос'}
      onClose={onClose}
    >
      <ErrorNote error={run.error} />
      {!data && run.isPending && <p className="muted">Загрузка…</p>}
      {data && (
        <>
          <dl className="detail">
            <dt>Объект</dt>
            <dd>{data.projectName}</dd>
            <dt>Администратор</dt>
            <dd>{data.personName}</dd>
            <dt>Анкета</dt>
            <dd>
              {data.templateName} · версия {data.version}
            </dd>
            <dt>Расписание</dt>
            <dd>{orDash(data.scheduleName)}</dd>
            <dt>Канал</dt>
            <dd>{CHANNEL_LABEL[data.channel as keyof typeof CHANNEL_LABEL] ?? data.channel}</dd>
            <dt>Статус</dt>
            <dd>
              <StatusBadge status={data.status} />
              {data.lateAnswer && <span className="badge badge--warn">ответ после срока</span>}
            </dd>
            <dt>Отправлен</dt>
            <dd>{formatInProjectTz(data.sentAt, data.projectTimezone)}</dd>
            <dt>Срок ответа</dt>
            {/* Дата без отношения к «сейчас» ничего не говорит: до неё ещё есть
                время, она уже прошла или ответ успел прийти вовремя. */}
            <dd>
              {formatInProjectTz(data.dueAt, data.projectTimezone)}
              <DeadlineNote
                dueAt={data.dueAt}
                completedAt={data.completedAt}
                status={data.status}
              />
            </dd>
            <dt>Завершён</dt>
            <dd>{formatInProjectTz(data.completedAt, data.projectTimezone)}</dd>
            <dt>Напоминаний</dt>
            <dd>{data.remindersSent}</dd>
            <dt>Инцидент</dt>
            {/* Номер — переход в карточку инцидента: иначе его приходится
                искать в соседнем разделе руками, копируя глазами. */}
            <dd>
              {data.incidentNumber ? (
                <Link to="/incidents" search={{ number: data.incidentNumber }}>
                  {data.incidentNumber}
                </Link>
              ) : (
                '—'
              )}
            </dd>
          </dl>

          <h3>Ответы</h3>
          {data.answers.length === 0 ? (
            <p className="muted">Ответов нет.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Вопрос</th>
                    <th>Ответ</th>
                    <th>Время</th>
                  </tr>
                </thead>
                <tbody>
                  {data.answers.map((answer) => (
                    <tr key={answer.questionKey}>
                      <td>
                        {answer.questionText}
                        {/* Отбивка — внешним отступом класса, а не пробелом
                            внутри тега: пробел попадал внутрь пилюли, и метка
                            липла к последнему слову вопроса. */}
                        {answer.scored && <span className="badge badge--tag">оценочный</span>}
                      </td>
                      {/* Свободный ответ переносится по словам: с nowrap
                          длинный комментарий растягивал таблицу и уезжал за
                          правый край карточки. */}
                      <td
                        className={
                          answer.valueNumber === null && answer.valueOption === null
                            ? 'cell-wrap'
                            : undefined
                        }
                      >
                        <AnswerValue answer={answer} />
                      </td>
                      <td>{formatInProjectTz(answer.answeredAt, data.projectTimezone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}

/** Значение ответа зависит от типа вопроса; пустых колонок в карточке не держим. */
function answerValue(answer: RunAnswer): string {
  if (answer.valueNumber !== null) return String(answer.valueNumber);
  if (answer.valueOption !== null) return answer.valueOption;
  return orDash(answer.valueText);
}

/**
 * Оценка показывается цветом шкалы — тем же, что у столбцов распределения
 * (§14): «2» среди пятёрок обязана бросаться в глаза, а не читаться цифрой
 * в ряду одинаковых. Неоценочный ответ остаётся обычным текстом.
 */
function AnswerValue({ answer }: { answer: RunAnswer }) {
  if (!answer.scored || answer.valueNumber === null) return <>{answerValue(answer)}</>;
  const tone =
    answer.valueNumber <= 2 ? ' rating--bad' : answer.valueNumber === 3 ? ' rating--warn' : '';
  return <span className={`rating${tone}`}>{answer.valueNumber}</span>;
}

/** Статус запуска пилюлей: завершён — спокойно, просрочен — тревожно. */
function StatusBadge({ status }: { status: RunStatus }) {
  const tone =
    status === 'COMPLETED'
      ? ' badge--ok'
      : status === 'EXPIRED' || status === 'CANCELLED'
        ? ' badge--err'
        : '';
  return <span className={`badge${tone}`}>{RUN_STATUS_LABEL[status]}</span>;
}

/**
 * Что означает срок ответа прямо сейчас. Считается в браузере намеренно:
 * это не показатель отчётности, а подсказка «истёк / сколько осталось»,
 * которая обязана меняться между запросами к серверу.
 */
function DeadlineNote({
  dueAt,
  completedAt,
  status,
}: {
  dueAt: string;
  completedAt: string | null;
  status: RunStatus;
}) {
  if (completedAt) {
    const inTime = new Date(completedAt).getTime() <= new Date(dueAt).getTime();
    return (
      <span className={`badge ${inTime ? 'badge--ok' : 'badge--warn'}`}>
        {inTime ? 'ответ в срок' : 'ответ после срока'}
      </span>
    );
  }
  if (status === 'CANCELLED') return null;

  const left = new Date(dueAt).getTime() - Date.now();
  if (left <= 0) return <span className="badge badge--err">срок истёк</span>;

  const hours = Math.floor(left / 3_600_000);
  const text =
    hours >= 24
      ? `остаётся ${plural(Math.floor(hours / 24), ['день', 'дня', 'дней'])}`
      : hours >= 1
        ? `остаётся ${plural(hours, ['час', 'часа', 'часов'])}`
        : 'меньше часа';
  return <span className="badge">{text}</span>;
}
